import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import RedBubble from 'ui-component/RedBubble';
import { usePendingTasks } from 'context/PendingTasksContext';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const { getCountForItem } = usePendingTasks();
    const pendingCount = getCountForItem(item);

    const Icon = item.icon;
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }
    if (item?.restricted) {
        listItemProps = { component: 'div' };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    const { pathname } = useLocation();

    const normalizedPath = pathname.replace(/\/+$/, '') || '/';
    const itemUrl = item.url ? item.url.replace(/\/+$/, '') || '/' : '';

    // active menu item tracking based on current URL path
    const isSelected = Boolean(
        itemUrl
            ? normalizedPath === itemUrl ||
              (itemUrl !== '/dashboard' && itemUrl !== '/userDashboard' && normalizedPath.startsWith(itemUrl + '/'))
            : customization.isOpen.findIndex((id) => id === item.id) > -1
    );

    useEffect(() => {
        if (
            itemUrl &&
            (normalizedPath === itemUrl ||
                (itemUrl !== '/dashboard' && itemUrl !== '/userDashboard' && normalizedPath.startsWith(itemUrl + '/')))
        ) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
    }, [normalizedPath, itemUrl, item.id, dispatch]);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateY(-5px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                },
                animation: level > 1 ? 'slideIn 0.3s ease-out forwards' : 'none',
                borderRadius: '10px',
                mb: 0.75,
                alignItems: 'center',
                backgroundColor: isSelected
                    ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important'
                    : level > 1
                    ? 'transparent'
                    : 'inherit',
                background: isSelected
                    ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important'
                    : 'transparent',
                color: isSelected ? '#ffffff !important' : '#334155',
                boxShadow: isSelected ? '0 4px 14px rgba(79, 70, 229, 0.32)' : 'none',
                py: level > 1 ? 1 : 1.15,
                px: 1.75,
                pl: level > 1 ? `${level * 20}px` : 1.75,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: isSelected
                        ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important'
                        : '#f1f5f9',
                    color: isSelected ? '#ffffff !important' : '#4f46e5',
                    transform: 'translateX(3px)',
                    '& .MuiListItemIcon-root': {
                        color: isSelected ? '#ffffff' : item?.color || '#4f46e5'
                    }
                }
            }}
            selected={isSelected}
            onClick={(e) => {
                if (item?.restricted) {
                    e.preventDefault();
                    import('react-toastify').then(({ toast }) => {
                        toast.error(`You are not authorize to access this ${item.title}`);
                    });
                    return;
                }
                itemHandler(item.id);
            }}
        >
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: !item?.icon ? 18 : 34,
                    color: isSelected ? '#ffffff' : item?.color || '#64748b',
                    transition: 'color 0.2s ease'
                }}
            >
                {itemIcon}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        variant={isSelected ? 'h5' : 'body1'}
                        sx={{
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? '#ffffff' : 'inherit',
                            fontSize: '0.875rem'
                        }}
                    >
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography
                            variant="caption"
                            sx={{
                                ...theme.typography.subMenuCaption,
                                color: isSelected ? 'rgba(255,255,255,0.8)' : '#94a3b8'
                            }}
                            display="block"
                            gutterBottom
                        >
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {pendingCount > 0 ? (
                <RedBubble
                    count={pendingCount}
                    tooltipText={`${pendingCount} pending task${pendingCount > 1 ? 's' : ''}`}
                    sx={{ ml: 1 }}
                />
            ) : item.chip ? (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                    sx={{
                        borderRadius: '6px',
                        fontWeight: 600,
                        backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : undefined,
                        color: isSelected ? '#ffffff' : undefined
                    }}
                />
            ) : null}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
